import React from 'react';
import { createPortal } from 'react-dom';
import { StatusBadge, ClientStatus } from './StatusBadge';
import { useSmartTooltipPosition } from '../../hooks/useSmartTooltipPosition';
import { SCORE_THRESHOLDS, SCORE_COLORS } from '../../../config/scoring';

interface GoalPerformance {
  goal_type: string;
  goal_name: string;
  current_target: number;
  variance_percent: number;
  days_met: number;
  days_with_data: number;
  completion_rate: number;
}

interface ClientStatusData {
  status: ClientStatus;
  badge: string;
  color: string;
  icon: string;
}

interface ActivityMetricsData {
  days_on_platform: number;
  days_since_last_entry: number | null;
  last_entry_date: string | null;
  entries_last_7_days: number;
  entries_last_lookback: number;
  lookback_period: number;
  engagement_rate_7d: number;
  engagement_rate_lookback: number;
  trend: 'improving' | 'stable' | 'declining';
  goal_performance: GoalPerformance[];
  overall_goal_completion: number;
}

interface ActivityMetricsTooltipProps {
  data: ActivityMetricsData | null;
  clientStatus?: ClientStatusData | null;
  visible: boolean;
  x: number;
  y: number;
  clientName?: string;
}

export const ActivityMetricsTooltip: React.FC<ActivityMetricsTooltipProps> = ({
  data,
  clientStatus,
  visible,
  x,
  y,
  clientName: _clientName
}) => {
  if (!visible || !data) return null;

  // Helper to get color based on completion rate
  const getCompletionColor = (rate: number): string => {
    // rate is 0.0-1.0; SCORE_THRESHOLDS are 0-100
    if (rate >= SCORE_THRESHOLDS.excellent / 100) return SCORE_COLORS.excellent;
    if (rate >= SCORE_THRESHOLDS.good / 100)      return SCORE_COLORS.good;
    if (rate >= SCORE_THRESHOLDS.fair / 100)       return SCORE_COLORS.fair;
    if (rate >= SCORE_THRESHOLDS.atRisk / 100)     return SCORE_COLORS.atRisk;
    return SCORE_COLORS.needsSupport;
  };

  // Helper to get trend arrow and color
  const getTrendDisplay = (trend: string) => {
    switch (trend) {
      case 'improving':
        return { arrow: '↑', color: '#10b981', label: 'Improving' };
      case 'declining':
        return { arrow: '↓', color: '#ef4444', label: 'Declining' };
      default:
        return { arrow: '→', color: '#64748b', label: 'Stable' };
    }
  };

  const trendDisplay = getTrendDisplay(data.trend);

  // Use smart tooltip positioning hook
  const tooltipPosition = useSmartTooltipPosition(x, y, {
    estimatedHeight: 350,
    side: 'left'
  });

  const tooltipElement = (
    <div
      style={{
        ...tooltipPosition,
        backgroundColor: '#1e1b3a',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-medium)',
        padding: '14px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        minWidth: '280px',
        maxWidth: '320px',
        fontSize: '12px',
        color: '#ffffff'
      }}
    >
      {/* Header */}
      <div style={{
        fontWeight: '600',
        marginBottom: '10px',
        fontSize: '13px',
        color: '#ffffff',
        borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
        paddingBottom: '8px'
      }}>
        Activity Metrics
        {data.lookback_period < 30 && (
          <span style={{ fontSize: '11px', fontWeight: '400', color: '#d1d5db', marginLeft: '6px' }}>
            (Last {data.lookback_period} days)
          </span>
        )}
        {data.lookback_period >= 30 && (
          <span style={{ fontSize: '11px', fontWeight: '400', color: '#d1d5db', marginLeft: '6px' }}>
            (Last 30 days)
          </span>
        )}
      </div>

      {/* Phase 4: Status Badge */}
      {clientStatus && (
        <div style={{
          marginBottom: '14px',
          display: 'flex',
          justifyContent: 'flex-start'
        }}>
          <StatusBadge
            status={clientStatus.status}
            badge={clientStatus.badge}
            color={clientStatus.color}
            icon={clientStatus.icon}
            size="large"
          />
        </div>
      )}

      {/* Recent Activity (7 days) */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <span style={{ fontSize: '11px', color: '#d1d5db' }}>Recent (7d):</span>
          <span style={{ fontSize: '11px', fontWeight: '600', color: '#ffffff' }}>
            {data.entries_last_7_days}/7 ({Math.round(data.engagement_rate_7d * 100)}%)
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '6px',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${Math.min(100, data.engagement_rate_7d * 100)}%`,
            height: '100%',
            backgroundColor: getCompletionColor(data.engagement_rate_7d),
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      {/* 30-Day Activity */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <span style={{ fontSize: '11px', color: '#d1d5db' }}>
            {data.lookback_period < 30 ? `${data.lookback_period}-Day:` : '30-Day:'}
          </span>
          <span style={{ fontSize: '11px', fontWeight: '600', color: '#ffffff' }}>
            {data.entries_last_lookback}/{data.lookback_period} ({Math.round(data.engagement_rate_lookback * 100)}%)
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '6px',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${Math.min(100, data.engagement_rate_lookback * 100)}%`,
            height: '100%',
            backgroundColor: getCompletionColor(data.engagement_rate_lookback),
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      {/* Trend Indicator */}
      <div style={{
        marginBottom: '12px',
        padding: '6px 10px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <span style={{ fontSize: '11px', color: '#d1d5db' }}>Trend:</span>
        <span style={{ fontSize: '16px', color: trendDisplay.color }}>{trendDisplay.arrow}</span>
        <span style={{ fontSize: '12px', fontWeight: '600', color: trendDisplay.color }}>
          {trendDisplay.label}
        </span>
      </div>

      {/* Divider */}
      <div style={{
        borderTop: '1px solid rgba(255, 255, 255, 0.15)',
        marginBottom: '10px'
      }} />

      {/* Goal Performance */}
      {data.goal_performance && data.goal_performance.length > 0 && (
        <>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#ffffff',
            marginBottom: '8px'
          }}>
            Goal Performance (Last {Math.min(data.days_on_platform || 30, 30)} days):
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            marginBottom: '10px'
          }}>
            {data.goal_performance.map((goal, index) => {
              const percentage = Math.round(goal.completion_rate * 100);
              const goalLabel = goal.goal_name.replace('Daily ', '');

              // Build target display with variance range for target-type goals
              let targetDisplay;
              if (goal.variance_percent && goal.variance_percent > 0) {
                // Target type with variance - show range (include parens in string since we want them closed before brackets)
                const lowerBound = Math.round(goal.current_target * (1 - goal.variance_percent / 100));
                const upperBound = Math.round(goal.current_target * (1 + goal.variance_percent / 100));

                if (goal.goal_type === 'daily_steps') {
                  targetDisplay = `(${(goal.current_target / 1000).toFixed(0)}k ±${goal.variance_percent}%) [${(lowerBound / 1000).toFixed(1)}k-${(upperBound / 1000).toFixed(1)}k]`;
                } else {
                  targetDisplay = `(${goal.current_target} ±${goal.variance_percent}%) [${lowerBound}-${upperBound}]`;
                }
              } else {
                // Over/under type - wrap in parens
                targetDisplay = goal.goal_type === 'daily_steps'
                  ? `(${(goal.current_target / 1000).toFixed(0)}k)`
                  : `(${goal.current_target})`;
              }

              return (
                <div key={index}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                    <span style={{ fontSize: '10px', color: '#d1d5db' }}>
                      {goalLabel} {targetDisplay}:
                    </span>
                    <span style={{ fontSize: '10px', fontWeight: '500', color: '#ffffff' }}>
                      {goal.days_met}/{goal.days_with_data}
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${percentage}%`,
                      height: '100%',
                      backgroundColor: getCompletionColor(goal.completion_rate),
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Overall Completion */}
          <div style={{
            padding: '8px 10px',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '11px', fontWeight: '600', color: '#ffffff' }}>
              Overall:
            </span>
            <span style={{
              fontSize: '13px',
              fontWeight: '700',
              color: getCompletionColor(data.overall_goal_completion)
            }}>
              {Math.round(data.overall_goal_completion * 100)}% completion
            </span>
          </div>
        </>
      )}

      {data.goal_performance && data.goal_performance.length === 0 && (
        <div style={{
          fontSize: '11px',
          color: '#9ca3af',
          fontStyle: 'italic',
          textAlign: 'center',
          padding: '8px'
        }}>
          No active goals
        </div>
      )}
    </div>
  );

  return createPortal(tooltipElement, document.body);
};
